import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  NO_VALUE_DISPLAY,
  passengerSelfRemovedCountSelector,
  tableRowsCountSF,
  UserEditDisplayModeEnum,
  userEditedByMeNotTodayCountSelector,
  userEditedByMeTodayCountSelector,
  userEditedNotByMeNotTodayCountSelector,
  userEditedNotByMeTodayCountSelector,
  userEditedRidesCountSelector,
} from "@ryde/shared";

export const useUserUpdaterRideToggle = () => {
  const { entity } = usePageContext();
  const alteredCount = useRecoilValue(userEditedRidesCountSelector);
  const passengerSelfRemovedCount = useRecoilValue(
    passengerSelfRemovedCountSelector,
  );
  const alteredByMeTodayCount = useRecoilValue(
    userEditedByMeTodayCountSelector,
  );
  const alteredNotByMeTodayCount = useRecoilValue(
    userEditedNotByMeTodayCountSelector,
  );
  const alteredByMeNotTodayCount = useRecoilValue(
    userEditedByMeNotTodayCountSelector,
  );

  const alteredNotByMeNotTodayCount = useRecoilValue(
    userEditedNotByMeNotTodayCountSelector,
  );
  const allRidesCount = useRecoilValue(tableRowsCountSF(entity));
  const { t } = useTranslation();

  const ridesCountByViewMode = useCallback(
    (value: UserEditDisplayModeEnum) => {
      switch (value) {
        case UserEditDisplayModeEnum.PassengerSelfRemoved:
          return passengerSelfRemovedCount || 0;
        case UserEditDisplayModeEnum.AlteredByMeTodayRides:
          return alteredByMeTodayCount || 0;
        case UserEditDisplayModeEnum.AlteredNotByMeTodayRides:
          return alteredNotByMeTodayCount || 0;
        case UserEditDisplayModeEnum.AlteredByMeNotTodayRides:
          return alteredByMeNotTodayCount || 0;
        case UserEditDisplayModeEnum.AlteredNotByMeNotTodayRides:
          return alteredNotByMeNotTodayCount || 0;
        case UserEditDisplayModeEnum.AlteredRides:
          return alteredCount || 0;
        case UserEditDisplayModeEnum.NotAlteredRides:
          return allRidesCount - alteredCount;
        default:
          return NO_VALUE_DISPLAY;
      }
    },
    [
      allRidesCount,
      alteredByMeNotTodayCount,
      alteredByMeTodayCount,
      alteredCount,
      alteredNotByMeNotTodayCount,
      alteredNotByMeTodayCount,
      passengerSelfRemovedCount,
    ],
  );

  const optionTextByValue = (value: UserEditDisplayModeEnum): string => {
    switch (value) {
      case UserEditDisplayModeEnum.AlteredRides:
        return t(`selectListLabel.${UserEditDisplayModeEnum.AlteredRides}`);
      case UserEditDisplayModeEnum.AlteredByMeRides:
        return t(`selectListLabel.${UserEditDisplayModeEnum.AlteredByMeRides}`);
      case UserEditDisplayModeEnum.AlteredByMeTodayRides:
        return t(
          `selectListLabel.${UserEditDisplayModeEnum.AlteredByMeTodayRides}`,
        );
      case UserEditDisplayModeEnum.AlteredNotByMeTodayRides:
        return t(
          `selectListLabel.${UserEditDisplayModeEnum.AlteredNotByMeTodayRides}`,
        );
      case UserEditDisplayModeEnum.AlteredNotByMeRides:
        return t(
          `selectListLabel.${UserEditDisplayModeEnum.AlteredNotByMeRides}`,
        );
      case UserEditDisplayModeEnum.AlteredByMeNotTodayRides:
        return t(
          `selectListLabel.${UserEditDisplayModeEnum.AlteredByMeNotTodayRides}`,
        );
      case UserEditDisplayModeEnum.AlteredNotByMeNotTodayRides:
        return t(
          `selectListLabel.${UserEditDisplayModeEnum.AlteredNotByMeNotTodayRides}`,
        );
      case UserEditDisplayModeEnum.NotAlteredRides:
        return t(`selectListLabel.${UserEditDisplayModeEnum.NotAlteredRides}`);
      case UserEditDisplayModeEnum.PassengerSelfRemoved:
        return t(
          `selectListLabel.${UserEditDisplayModeEnum.PassengerSelfRemoved}`,
        );
      default:
        return t(`selectListLabel.${UserEditDisplayModeEnum.AllRides}`);
    }
  };

  return { ridesCountByViewMode, optionTextByValue };
};
